/**
 * Tabs — shadcn/ui primitive (TruSpace).
 *
 * Radix-backed tabs with full keyboard support (arrow keys, Home/End)
 * and correct `role="tab"` / `aria-selected` wiring. The active tab
 * indicator is the filled `bg-background` pill on the muted track, which
 * reads unambiguously in BOTH themes (a11y.mdc §5 — never color-only).
 *
 * Mobile overflow: `TabsList` scrolls horizontally with a hidden
 * scrollbar so labels never truncate (task spec §9). Pair with
 * `useSearchParams` for deep-linkable tabs (state.mdc §6).
 *
 * @see https://ui.shadcn.com/docs/components/tabs
 */
import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { ComponentPropsWithoutRef } from 'react';
import { cn } from '@/lib/cn';

/* eslint-disable-next-line react-refresh/only-export-components */
export const Tabs = TabsPrimitive.Root;

export function TabsList({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn(
        // `no-scrollbar` hides the bar; horizontal scroll keeps labels
        // intact on narrow viewports instead of truncating them.
        'inline-flex h-10 max-w-full items-center justify-start gap-1 overflow-x-auto rounded-lg bg-muted p-1 text-muted-foreground no-scrollbar',
        className,
      )}
      {...props}
    />
  );
}

export function TabsTrigger({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium ring-offset-background transition-all',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm',
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({
  className,
  ...props
}: ComponentPropsWithoutRef<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      className={cn(
        'mt-4 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        className,
      )}
      {...props}
    />
  );
}
